import React, { createContext, useState, useContext, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, useTheme as useMuiTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { apiService } from '../services/api.service';
import { useTenant } from '../context/TenantContext';

// Create a context for the theme
const ThemeContext = createContext();

// Default theme configuration
const defaultTheme = {
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff4081',
      dark: '#9a0036',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 4,
  },
  custom: {
    appName: 'Supply Chain Management',
    footerText: '© 2023 Supply Chain Inc. All rights reserved.',
    navbarPosition: 'left',
    dashboardLayout: 'standard'
  }
};

export const ThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);
  
  // Get tenant information
  const { tenantAlias, isLoading: isTenantLoading } = useTenant();

  useEffect(() => {
    // Only fetch theme when tenant is available
    if (isTenantLoading || !tenantAlias) {
      setLoading(false); // Not loading if we're using default theme
      return; // Wait until tenant is loaded or use default
    }
    
    const fetchTenantTheme = async () => {
      try {
        setLoading(true);
        console.log(`Fetching theme for tenant alias: ${tenantAlias}`);
        
        // Get tenant theme using the new endpoint
        try {
          const themeResponse = await apiService.get(`/ux/themes/${tenantAlias}`);
          
          if (themeResponse && Object.keys(themeResponse).length > 0) {
            console.log('Theme loaded successfully:', themeResponse);
            
            // Merge with default theme to ensure all required properties exist
            const mergedTheme = {
              ...defaultTheme,
              ...themeResponse,
              palette: {
                ...defaultTheme.palette,
                ...(themeResponse.palette || {}),
              },
              typography: {
                ...defaultTheme.typography,
                ...(themeResponse.typography || {}),
              },
              custom: {
                ...defaultTheme.custom,
                ...(themeResponse.custom || {}),
              }
            };
            
            setThemeConfig(mergedTheme);
          }
        } catch (themeError) {
          console.error('Failed to load theme, using default:', themeError);
        }
      } catch (error) {
        console.error('Failed to load tenant info:', error);
        // Fall back to default theme
      } finally {
        setLoading(false);
      }
    };
    
    fetchTenantTheme();
  }, [tenantAlias, isTenantLoading]); // Depend on tenant alias and loading state
  
  // Create the MUI theme from our configuration
  const theme = createTheme(themeConfig);
  
  // Provide both the theme object and the configuration
  const contextValue = {
    theme,
    themeConfig,
    loading: loading || isTenantLoading, // Consider theme loading if tenant is still loading
    custom: themeConfig.custom || defaultTheme.custom, // Expose custom theme properties
  };
  
  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  const muiTheme = useMuiTheme(); // Always call this hook, regardless of context
  
  if (!context) {
    // Return the default MUI theme if our context isn't available
    return { 
      theme: muiTheme, 
      themeConfig: defaultTheme,
      loading: false,
      custom: defaultTheme.custom
    };
  }
  
  return context;
}; 